/* eslint-disable react/prop-types */
import React, {memo, useContext, useEffect, useMemo, useRef, useState} from "react";
import {ComposableMap, Geographies, Geography, Line, Marker} from "react-simple-maps";
import {geoPatterson} from "d3-geo-projection";
import {geoCircle} from "d3-geo";
import {GlobeState} from "lib/context/GlobeProvider";
import {AppState} from "lib/context/AppProvider";
import {SELECTED_SAT_COLOR, UNSELECTED_SAT_COLOR} from "lib/helpers/globeview/useGlobeSettings";
import {parseDate} from "lib/helpers/utilities";
import {useUtilities} from "lib/helpers/globeview/useUtilities";
import {IoIosSunny} from "react-icons/io";


const MAX_POINTS = 200;
const ORBIT_UPDATE_PERIOD = 60 * 1000; // update path once a minute instead of every 5s

const projection = geoPatterson()
    .translate([400, 225])
    .scale(128);

const nthElement = (arr, n) => {
    const result = [];
    for (let i = 0; i < arr.length; i += n) {
        result.push(arr[i]);
    }
    return result;
};

export const GS_COLORS = [
    {
        label: "UHF",
        key: "UHF",
        color: "#0000FF",
    },
    {
        label: "QV",
        key: "QV",
        color: "#FF00FF",
    },
    {
        label: "KSAT",
        key: "KSAT",
        color: "#FF0000",
    },
    {
        label: "LeafSpace",
        key: "LS-",
        color: "#00FF00",
    },
];

const getGSColor = (name) => {
    for (const mapping of GS_COLORS) {
        if (name.toUpperCase().includes(mapping.key)) {
            return mapping.color;
        }
    }
    return null;
};

// memoizing the component so it doesn't rerender every time the parent does
const GroundStationViz = memo(({allGroundstations}) => {
    return <>
        {allGroundstations.map((gs) => {
            const color = getGSColor(gs.name);
            return color && <Marker
                key={gs.name}
                coordinates={[gs.lng, gs.lat]}>
                <circle r={4} fill={color} />
            </Marker>;
        })}
    </>;
});
GroundStationViz.displayName = "GroundStationViz";

const OverpassSegments = memo(({overpasses, orbit, isSelected}) => {
    const passes = useMemo(() => {
        // if (!isSelected) return [];
        if (!(orbit?.length > 0 && overpasses?.length > 0)) return [];
        const start = orbit[0].time;
        const end = orbit[orbit.length - 1].time;

        const passes = overpasses.filter((o) => {
            return parseDate(o.los) > start && parseDate(o.aos) < end && getGSColor(o.gs_name);
        });

        const passCoords = passes.map((pass) => {
            const aos = parseDate(pass.aos);
            const los = parseDate(pass.los);
            let aosPoint = orbit.findIndex((p) => p.time > aos);
            let losPoint = orbit.findIndex((p) => p.time > los);

            if (aosPoint < 0) aosPoint = 0;
            if (losPoint < 0) losPoint = orbit.length;

            const segment = orbit.filter((p, i) => i >= aosPoint && i < losPoint);

            return {
                coords: segment.map((pos) => [pos.lon, pos.lat]),
                color: getGSColor(pass.gs_name),
                name: pass.gs_name,
            };
        });

        return passCoords;
    }, [orbit, overpasses, isSelected]);

    return <>
        {passes.map((pass) => <Line
            key={pass.name}
            coordinates={pass.coords}
            stroke={pass.color}
            strokeWidth={isSelected ? 3 : 1}
            strokeOpacity={1}
        />)}
    </>;
});
OverpassSegments.displayName = "OverpassSegments";

const SunViz = () => {
    const {store} = useContext(AppState);
    const {currentTime} = store;
    const {sunPosAt} = useUtilities(currentTime);
    const color = "rgb(200, 100, 0)";

    const sunPos = sunPosAt();

    const sun = [sunPos[1], sunPos[0]];

    const antipode = ([longitude, latitude]) => [longitude + 180, -latitude];

    const day = geoCircle().radius(90).center(sun)();
    const night = geoCircle().radius(90).center(antipode(sun))();

    const geoJsonData = {
        type: "FeatureCollection",
        features: [
            {
                type: "Feature",
                geometry: day,
            },
            {
                type: "Feature",
                geometry: night,
            },
        ],
    };

    return (<>
        <Geographies geography={geoJsonData}>
            {({geographies}) =>
                geographies.map((geo, i) => (
                    <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={i === 0 ? "rgba(200, 100, 0, 0.2)" : "rgba(0, 87, 115, 0.2)"}
                        stroke={"rgba(0,0,0,0)"}
                        // stroke={i === 0 ? "rgba(200, 100, 0, 0.8)" : "rgba(0,0,0,0)"}
                        // strokeDasharray="10, 10" // 5px dash, 5px gap
                        tabIndex={-1}
                        style={{
                            default: {outline: "none"},
                            hover: {outline: "none"},
                            pressed: {outline: "none"},
                        }}
                    />
                ))
            }
        </Geographies>
        <Marker coordinates={sun} style={{
            display: "flex",
            alignItems: "center",
            justifyItems: "center",
        }}>
            <g
                transform="translate(-10 -10)">
                <IoIosSunny size={20} color={color} />
            </g>
        </Marker>
    </>
    );
};

const OrbitPath = memo(({orbit, color, isSelected}) => {
    const coords = useMemo(() => orbit.map((pos) => [pos.lon, pos.lat]), [orbit]);

    return <Line
        coordinates={coords}
        stroke={color}
        strokeWidth={isSelected ? 3 : 1}
        strokeOpacity={1}
    />;
});
OrbitPath.displayName = "OrbitPath";

const GroundTrackViz = ({sat, color, textColor, isSelected}) => {
    const {globe} = useContext(GlobeState);
    const {
        satOrbits,
    } = globe;

    const name = sat.sat_name;

    const [reducedOrbit, setReducedOrbit] = useState([]);
    const lastSet = useRef(null);

    const state = satOrbits?.[name];

    useEffect(() => {
        if (lastSet.current && Date.now() - lastSet.current <= ORBIT_UPDATE_PERIOD) {
            return;
        }

        let orbit = state?.orbit;
        if (!orbit) return;

        const skip = Math.floor(orbit.length / MAX_POINTS);
        if (skip > 1) {
            orbit = nthElement(orbit, skip);
        }
        if (orbit.length > 0) {
            setReducedOrbit(orbit);
            lastSet.current = Date.now();
        }
    }, [state?.orbit]);

    if (!state) return null;

    return (<>
        <OrbitPath
            orbit={reducedOrbit}
            color={color}
            isSelected={isSelected}
        />
        <OverpassSegments
            overpasses={sat.overpasses}
            orbit={reducedOrbit}
            isSelected={isSelected}
        />
        <Marker coordinates={[state.pos.lng, state.pos.lat + (isSelected ? 6 : 3)]}>
            <text textAnchor="middle" fill={textColor} fontWeight="bold" fontSize={isSelected ? 16 : 14}>
                {name}
            </text>
        </Marker>
        <Marker coordinates={[state.pos.lng, state.pos.lat]}>
            <circle r={isSelected ? 8 : 4} fill={textColor} />
        </Marker>
    </>);
};

const GroundTrack = () => {
    const {store} = useContext(AppState);
    const {satellites, selectedSatIndex} = store;

    const {globe} = useContext(GlobeState);
    const {
        allGroundstations,
    } = globe;

    const sorted = useMemo(() => {
        // ensures selected sat is drawn over others
        const satsMapped = satellites
            .map((s, i) => ({...s, index: i}));
        const sats = satsMapped.filter((s) => s.index !== selectedSatIndex);
        sats.push(...satsMapped.filter((s) => s.index === selectedSatIndex));
        return sats;
    }, [selectedSatIndex, JSON.stringify(satellites)]);

    return (
        <ComposableMap
            projection={projection}
        >
            <Geographies geography="/satview/features.json">
                {({geographies}) =>
                    geographies.map((geo) => (
                        <Geography
                            key={geo.rsmKey}
                            geography={geo}
                            fill="#888888"
                            stroke="#333333"
                            tabIndex={-1}
                            style={{
                                default: {outline: "none"},
                                hover: {outline: "none"},
                                pressed: {outline: "none"},
                            }}
                        />
                    ))
                }
            </Geographies>

            <SunViz />

            <GroundStationViz allGroundstations={allGroundstations} />
            {sorted.map((sat) =>
                <GroundTrackViz
                    key={sat.sat_name}
                    sat={sat}
                    isSelected={sat.index === selectedSatIndex}
                    color={sat.index === selectedSatIndex ? SELECTED_SAT_COLOR[0] : UNSELECTED_SAT_COLOR[1]}
                    textColor={sat.index === selectedSatIndex ? SELECTED_SAT_COLOR[0] : UNSELECTED_SAT_COLOR[0]}
                />)
            }
        </ComposableMap>
    );
};

export default GroundTrack;
